import React, { useMemo, useState } from 'react'
import {
  Dialog,
  DialogTitle,
  DialogContent,
  Button,
  Box,
  Typography,
  Tabs,
  Tab,
  CircularProgress,
  Card,
  CardContent,
  LinearProgress,
  Chip,
  Divider,
} from '@mui/material'
import {
  Close as CloseIcon,
  Download as DownloadIcon,
  CheckCircle as CheckCircleIcon,
  ReportProblem as ReportProblemIcon,
} from '@mui/icons-material'
import { useQuery } from '@tanstack/react-query'
import { apiClient } from '@/core/api/apiClient'
import PhotoComparisonDialog from '../../../PhotoUpload/components/dialogs/PhotoComparisonDialog'
import { LegPhotoAdminUploadGrid } from './LegPhotoAdminUploadGrid'
import { usePhotoTaskTemplates } from '@/hooks/admin/usePhotoTaskTemplates'
import { useSnackbar } from '@/hooks/common/useSnackbar'
import { useDamageReports, useDamageReportsByPhotoIds } from '@/hooks/useDamageReports'
import type { ShipmentPlanVehicleDamage } from '@otw/models/logistics'
import { useTransporterGroupOwners } from '@/hooks/admin/useTransporterGroups'

const SHOP_TRANSPORT_COMPANY_ID = 'shop'
const CUSTOMER_PICKUP_COMPANY_ID = 'customer_pickup'

interface LegItem {
  fromLabel: string
  toLabel: string
  company_id?: string
  /** When 'repair', pre-delivery approval is not required */
  to_type?: 'pickup' | 'repair'
  /** Notes from the most recent status_history entry (e.g. "Pre-delivery photos completed") */
  latestStatusNotes?: string
}

interface LegPhoto {
  id: string
  angle: string
  url?: string
  storagePath?: string
  mediaType?: 'photo' | 'video'
  uploadedAt?: unknown
  uploadedBy?: string
  isReplaced?: boolean
  replacedAt?: unknown
  originalUploadedAt?: unknown
}

interface LegPhotosDialogProps {
  open: boolean
  onClose: () => void
  planId: string
  legItems: LegItem[]
  /** Open to this leg tab (0-based) */
  initialLegIndex?: number
  /** Vehicle condition report at plan level */
  vehicle_damage?: ShipmentPlanVehicleDamage | null
  /** When pre-delivery complete and to is not repair, allow approve/reject. Called with planId, legIndex. */
  onApprovePreDelivery?: (planId: string, legIndex: number) => Promise<void>
  /** When pre-delivery rejected, open plan dialog to add repair; rejection status is saved on dialog save. Called with planId, legIndex. */
  onRejectPreDelivery?: (planId: string, legIndex: number) => void
}

const LegPhotosDialog: React.FC<LegPhotosDialogProps> = ({
  open,
  onClose,
  planId,
  legItems,
  initialLegIndex = 0,
  vehicle_damage,
  onApprovePreDelivery,
  onRejectPreDelivery,
}) => {
  const [legTab, setLegTab] = useState(initialLegIndex)
  const [selectedPhotos, setSelectedPhotos] = useState<Record<string, boolean>>({})
  const [isApproving, setIsApproving] = useState(false)
  const [comparisonState, setComparisonState] = useState<{
    isOpen: boolean
    photoIndex: number
    mediaType: 'photo' | 'video'
    flow: 'pre' | 'post'
  }>({ isOpen: false, photoIndex: 0, mediaType: 'photo', flow: 'pre' })
  const [isDownloading, setIsDownloading] = useState(false)
  const [isInspectingAll, setIsInspectingAll] = useState(false)
  const { showSuccess, showError } = useSnackbar()
  const { getTemplateByFlow } = usePhotoTaskTemplates()
  const { generateAndSaveDamageReport } = useDamageReports()

  React.useEffect(() => {
    if (open) {
      setLegTab(initialLegIndex)
      setSelectedPhotos({})
    }
  }, [open, initialLegIndex])

  const legIndex = Math.min(legTab, legItems.length - 1)
  const activeLeg = legItems[legIndex]
  const companyId = activeLeg?.company_id
  const transporterGroupIds = useMemo(
    () =>
      companyId &&
      companyId !== SHOP_TRANSPORT_COMPANY_ID &&
      companyId !== CUSTOMER_PICKUP_COMPANY_ID
        ? [companyId]
        : [],
    [companyId]
  )
  const { data: transporterGroupOwners } = useTransporterGroupOwners(transporterGroupIds)
  const activeLegCompanyName = useMemo(() => {
    if (!companyId) return 'Unassigned'
    if (companyId === SHOP_TRANSPORT_COMPANY_ID) return 'Shop transport'
    if (companyId === CUSTOMER_PICKUP_COMPANY_ID) return 'Customer pickup'

    const owner = transporterGroupOwners?.[companyId]
    if (!owner) return companyId
    return (
      owner.group_name ||
      [owner.first_name, owner.last_name].filter(Boolean).join(' ').trim() ||
      owner.email ||
      companyId
    )
  }, [companyId, transporterGroupOwners])

  const preTemplate = getTemplateByFlow('preDelivery', companyId)
  const postTemplate = getTemplateByFlow('postDelivery', companyId)

  const { data: preData, isLoading: isLoadingPre } = useQuery({
    queryKey: ['leg_photos_admin', planId, legIndex, 'preDelivery'],
    queryFn: () => apiClient.portal.admin.listLegPhotos(planId, legIndex, 'preDelivery'),
    enabled: open && !!planId && legIndex >= 0,
  })

  const { data: postData, isLoading: isLoadingPost } = useQuery({
    queryKey: ['leg_photos_admin', planId, legIndex, 'postDelivery'],
    queryFn: () => apiClient.portal.admin.listLegPhotos(planId, legIndex, 'postDelivery'),
    enabled: open && !!planId && legIndex >= 0,
  })

  const prePhotos = useMemo(
    () => (preData?.photos ?? []).filter((p) => (p as LegPhoto).mediaType !== 'video') as LegPhoto[],
    [preData]
  )
  const preVideos = useMemo(
    () => (preData?.photos ?? []).filter((p) => (p as LegPhoto).mediaType === 'video') as LegPhoto[],
    [preData]
  )
  const postPhotos = useMemo(
    () => (postData?.photos ?? []).filter((p) => (p as LegPhoto).mediaType !== 'video') as LegPhoto[],
    [postData]
  )
  const postVideos = useMemo(
    () => (postData?.photos ?? []).filter((p) => (p as LegPhoto).mediaType === 'video') as LegPhoto[],
    [postData]
  )

  const allPhotoIds = useMemo(
    () => [...prePhotos, ...postPhotos].map((p) => p.id).filter(Boolean),
    [prePhotos, postPhotos]
  )
  const { data: damageReportsByPhotoId = {} } = useDamageReportsByPhotoIds(allPhotoIds)

  const getProgress = (
    photos: LegPhoto[],
    videos: LegPhoto[],
    template: ReturnType<typeof getTemplateByFlow>
  ) => {
    const photoPositions = template?.tasks?.filter((t) => t.active && t.mediaType !== 'video') ?? []
    const videoPositions = template?.tasks?.filter((t) => t.active && t.mediaType === 'video') ?? []
    const totalPhotos = photoPositions.length
    const totalVideos = videoPositions.length
    const totalMedia = totalPhotos + totalVideos
    const uniquePhotoAngles = new Set(photos.map((p) => p.angle))
    const uniqueVideoAngles = new Set(videos.map((v) => v.angle))
    const completedPhotos = uniquePhotoAngles.size
    const completedVideos = uniqueVideoAngles.size
    const completedMedia = completedPhotos + completedVideos
    const percentage =
      totalMedia > 0 ? Math.round((completedMedia / totalMedia) * 100) : totalPhotos > 0 ? Math.round((completedPhotos / totalPhotos) * 100) : 0
    return {
      completedMedia,
      totalMedia: totalMedia || completedMedia,
      completedPhotos,
      totalPhotos: totalPhotos || completedPhotos,
      completedVideos,
      totalVideos: totalVideos || completedVideos,
      percentage: totalMedia > 0 ? percentage : 0,
    }
  }

  const preProgress = getProgress(prePhotos, preVideos, preTemplate)
  const postProgress = getProgress(postPhotos, postVideos, postTemplate)

  const isLoading = isLoadingPre || isLoadingPost
  const latestNoteIsPreDeliveryCompleted =
    activeLeg?.latestStatusNotes === 'Pre-delivery photos completed'
  const allPreDeliveryUploadedByAdmin = useMemo(() => {
    const allPre = [...prePhotos, ...preVideos]
    return allPre.length > 0 && allPre.every((p) => p.uploadedBy === 'admin')
  }, [prePhotos, preVideos])
  const legToIsRepair = activeLeg?.to_type === 'repair'
  const canApproveReject =
    latestNoteIsPreDeliveryCompleted &&
    !allPreDeliveryUploadedByAdmin &&
    !legToIsRepair &&
    (onApprovePreDelivery != null || onRejectPreDelivery != null)

  const handleApprovePreDelivery = async () => {
    if (!onApprovePreDelivery || legIndex < 0) return
    setIsApproving(true)
    try {
      await onApprovePreDelivery(planId, legIndex)
      showSuccess('Pre-delivery photos approved')
      onClose()
    } catch {
      showError('Failed to approve photos')
    } finally {
      setIsApproving(false)
    }
  }

  const handleRejectPreDelivery = () => {
    if (!onRejectPreDelivery || legIndex < 0) return
    onRejectPreDelivery(planId, legIndex)
    onClose()
  }

  const downloadPhoto = async (url: string, filename: string) => {
    const response = await fetch(url)
    const blob = await response.blob()
    const downloadUrl = window.URL.createObjectURL(blob)
    const link = document.createElement('a')
    link.href = downloadUrl
    link.download = filename
    document.body.appendChild(link)
    link.click()
    document.body.removeChild(link)
    window.URL.revokeObjectURL(downloadUrl)
  }

  const handlePhotoSelect = (photoId: string) => {
    setSelectedPhotos((prev) => ({ ...prev, [photoId]: !(prev[photoId] ?? false) }))
  }

  const hasSelectedPhotos = () => Object.values(selectedPhotos).some((s) => s)

  const handleDownloadSelected = async () => {
    if (!hasSelectedPhotos()) return
    const selected = [...prePhotos, ...postPhotos].filter((p) => selectedPhotos[p.id])
    if (selected.length === 0) return
    setIsDownloading(true)
    try {
      let successCount = 0
      let errorCount = 0
      for (const photo of selected) {
        const url = photo.url || photo.storagePath
        if (!url) {
          errorCount++
          continue
        }
        try {
          const ext = (photo.mediaType === 'video' ? 'mp4' : 'jpg') as string
          const filename = `${photo.angle.replace(/\s+/g, '_')}_${Date.now()}.${ext}`
          await downloadPhoto(url, filename)
          successCount++
        } catch {
          errorCount++
        }
      }
      if (successCount > 0) showSuccess(`Downloaded ${successCount} file${successCount === 1 ? '' : 's'}`)
      if (errorCount > 0) showError(`Failed to download ${errorCount} file${errorCount === 1 ? '' : 's'}`)
      setSelectedPhotos({})
    } finally {
      setIsDownloading(false)
    }
  }

  const handlePhotoClick = (photo: LegPhoto, allPhotos: LegPhoto[], flow: 'pre' | 'post') => {
    const idx = allPhotos.findIndex((p) => p.id === photo.id)
    if (idx >= 0) setComparisonState({ isOpen: true, photoIndex: idx, mediaType: 'photo', flow })
  }

  const handleVideoClick = (video: LegPhoto, allVideos: LegPhoto[], flow: 'pre' | 'post') => {
    const idx = allVideos.findIndex((v) => v.id === video.id)
    if (idx >= 0) setComparisonState({ isOpen: true, photoIndex: idx, mediaType: 'video', flow })
  }

  const handleInspectAll = async () => {
    const photosToInspect = [...prePhotos, ...postPhotos].filter((p) => p.url || p.storagePath)
    if (photosToInspect.length === 0) {
      showError('No photos available to inspect')
      return
    }
    setIsInspectingAll(true)
    let successCount = 0
    let errorCount = 0
    try {
      for (const photo of photosToInspect) {
        const url = photo.url || photo.storagePath
        if (!url) {
          errorCount++
          continue
        }
        try {
          await generateAndSaveDamageReport(photo.id, url, photo.angle, { silent: true })
          successCount++
        } catch {
          errorCount++
        }
      }
      if (successCount > 0) {
        showSuccess(
          `Inspected ${successCount} photo${successCount === 1 ? '' : 's'}${
            errorCount > 0 ? ` (${errorCount} failed)` : ''
          }`
        )
      } else if (errorCount > 0) {
        showError(`Failed to inspect ${errorCount} photo${errorCount === 1 ? '' : 's'}`)
      }
    } finally {
      setIsInspectingAll(false)
    }
  }

  const getComparisonPhotos = () => {
    const flow = comparisonState.flow
    if (comparisonState.mediaType === 'video') {
      return flow === 'pre' ? preVideos : postVideos
    }
    return flow === 'pre' ? prePhotos : postPhotos
  }

  return (
    <Dialog open={open} onClose={onClose} maxWidth="lg" fullWidth>
      <DialogTitle sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', flexWrap: 'wrap', gap: 1 }}>
        <Typography variant="h6">Leg Photos</Typography>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap' }}>
          {canApproveReject && (
            <>
              <Button
                size="small"
                variant="contained"
                color="success"
                onClick={handleApprovePreDelivery}
                disabled={isApproving}
                sx={{ textTransform: 'none' }}
              >
                {isApproving ? 'Approving...' : 'Approve Photos'}
              </Button>
              <Button
                size="small"
                variant="contained"
                color="error"
                onClick={handleRejectPreDelivery}
                sx={{ textTransform: 'none' }}
              >
                Needs Repair
              </Button>
            </>
          )}
          <Button
            size="small"
            variant="outlined"
            color="warning"
            startIcon={
              isInspectingAll ? <CircularProgress size={16} color="inherit" /> : <ReportProblemIcon />
            }
            onClick={handleInspectAll}
            disabled={isInspectingAll || [...prePhotos, ...postPhotos].length === 0}
            sx={{ textTransform: 'none' }}
          >
            {isInspectingAll ? 'Inspecting...' : 'Inspect all'}
          </Button>
          <Button
            size="small"
            variant="outlined"
            startIcon={<DownloadIcon />}
            onClick={handleDownloadSelected}
            disabled={!hasSelectedPhotos() || isDownloading}
          >
            {isDownloading ? 'Downloading...' : 'Download selected'}
          </Button>
          <Button startIcon={<CloseIcon />} onClick={onClose} size="small">
            Close
          </Button>
        </Box>
      </DialogTitle>
      <DialogContent dividers sx={{ minHeight: 560, maxHeight: '80vh' }}>
        {legItems.length === 0 ? (
          <Typography color="text.secondary">No legs in this plan.</Typography>
        ) : (
          <>
            <Tabs
              value={legTab}
              onChange={(_, v) => setLegTab(v)}
              sx={{ mb: 2, borderBottom: 1, borderColor: 'divider' }}
            >
              {legItems.map((leg, idx) => (
                <Tab
                  key={idx}
                  label={`${leg.fromLabel} → ${leg.toLabel}`}
                  id={`leg-tab-${idx}`}
                  aria-controls={`leg-panel-${idx}`}
                />
              ))}
            </Tabs>

            {activeLeg && (
              <Box sx={{ display: 'flex', flexDirection: 'column', gap: 3 }}>
                <Box
                  sx={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: 1,
                    p: 1.25,
                    border: '1px solid',
                    borderColor: 'divider',
                    borderRadius: 1.5,
                    bgcolor: 'background.paper',
                  }}
                >
                  <Typography variant="body2" color="text.secondary">
                    Transport company:
                  </Typography>
                  <Typography variant="body2" fontWeight={600}>
                    {activeLegCompanyName}
                  </Typography>
                </Box>
                {isLoading ? (
                  <Box display="flex" justifyContent="center" py={4}>
                    <CircularProgress />
                  </Box>
                ) : (
                  <>
                    <Box>
                      <Typography variant="subtitle2" fontWeight={600} sx={{ mb: 1.5 }}>
                        Pre-delivery
                      </Typography>
                      <ProgressCard progress={preProgress} label="Pre-Delivery" />
                      <LegPhotoAdminUploadGrid
                        planId={planId}
                        legIndex={legIndex}
                        flow="preDelivery"
                        template={preTemplate}
                        photos={prePhotos}
                        videos={preVideos}
                        selectedPhotos={selectedPhotos}
                        onPhotoSelect={handlePhotoSelect}
                        onPhotoClick={(p) => handlePhotoClick(p as LegPhoto, prePhotos, 'pre')}
                        onVideoClick={(v) => handleVideoClick(v as LegPhoto, preVideos, 'pre')}
                        damageReportsByPhotoId={damageReportsByPhotoId}
                      />
                    </Box>
                    <Divider />
                    <Box>
                      <Typography variant="subtitle2" fontWeight={600} sx={{ mb: 1.5 }}>
                        Post-delivery
                      </Typography>
                      <ProgressCard progress={postProgress} label="Post-Delivery" />
                      <LegPhotoAdminUploadGrid
                        planId={planId}
                        legIndex={legIndex}
                        flow="postDelivery"
                        template={postTemplate}
                        photos={postPhotos}
                        videos={postVideos}
                        selectedPhotos={selectedPhotos}
                        onPhotoSelect={handlePhotoSelect}
                        onPhotoClick={(p) => handlePhotoClick(p as LegPhoto, postPhotos, 'post')}
                        onVideoClick={(v) => handleVideoClick(v as LegPhoto, postVideos, 'post')}
                        damageReportsByPhotoId={damageReportsByPhotoId}
                      />
                    </Box>

                    {/* Vehicle Damage Report */}
                    {vehicle_damage && (
                      <Card sx={{ mt: 3 }}>
                        <CardContent>
                          <Typography variant="h6" sx={{ fontWeight: 600, mb: 2, color: 'primary.main' }}>
                            Vehicle Damage Report
                          </Typography>

                          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                            <Box>
                              <Typography variant="body2" sx={{ fontWeight: 500, mb: 1 }}>
                                Does the truck have any damage?
                              </Typography>
                              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                <Box
                                  sx={{
                                    width: 12,
                                    height: 12,
                                    borderRadius: '50%',
                                    bgcolor: vehicle_damage.has_damage ? 'error.main' : 'success.main',
                                  }}
                                />
                                <Typography
                                  variant="body2"
                                  sx={{
                                    color: vehicle_damage.has_damage ? 'error.main' : 'success.main',
                                    fontWeight: 500,
                                  }}
                                >
                                  {vehicle_damage.has_damage ? 'Yes' : 'No'}
                                </Typography>
                              </Box>
                              {vehicle_damage.has_damage && vehicle_damage.description && (
                                <Box
                                  sx={{
                                    mt: 1,
                                    p: 2,
                                    bgcolor: 'error.50',
                                    borderRadius: 1,
                                    border: '1px solid',
                                    borderColor: 'error.200',
                                  }}
                                >
                                  <Typography
                                    variant="body2"
                                    sx={{ fontWeight: 500, mb: 1, color: 'error.main' }}
                                  >
                                    Damage Description:
                                  </Typography>
                                  <Typography variant="body2" sx={{ color: 'text.secondary' }}>
                                    {vehicle_damage.description}
                                  </Typography>
                                </Box>
                              )}
                            </Box>

                            <Box>
                              <Typography variant="body2" sx={{ fontWeight: 500, mb: 1 }}>
                                Does the truck have working cameras?
                              </Typography>
                              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                <Box
                                  sx={{
                                    width: 12,
                                    height: 12,
                                    borderRadius: '50%',
                                    bgcolor: vehicle_damage.has_working_cameras
                                      ? 'success.main'
                                      : 'error.main',
                                  }}
                                />
                                <Typography
                                  variant="body2"
                                  sx={{
                                    color: vehicle_damage.has_working_cameras
                                      ? 'success.main'
                                      : 'error.main',
                                    fontWeight: 500,
                                  }}
                                >
                                  {vehicle_damage.has_working_cameras ? 'Yes' : 'No'}
                                </Typography>
                              </Box>
                            </Box>

                            <Box>
                              <Typography variant="body2" sx={{ fontWeight: 500, mb: 1 }}>
                                Does the truck have shelves?
                              </Typography>
                              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                <Box
                                  sx={{
                                    width: 12,
                                    height: 12,
                                    borderRadius: '50%',
                                    bgcolor: vehicle_damage.has_shelves ? 'success.main' : 'error.main',
                                  }}
                                />
                                <Typography
                                  variant="body2"
                                  sx={{
                                    color: vehicle_damage.has_shelves ? 'success.main' : 'error.main',
                                    fontWeight: 500,
                                  }}
                                >
                                  {vehicle_damage.has_shelves ? 'Yes' : 'No'}
                                </Typography>
                              </Box>
                            </Box>

                            {vehicle_damage.repair_shop_notes?.trim() ? (
                              <Box
                                sx={{
                                  p: 2,
                                  bgcolor: 'info.50',
                                  borderRadius: 1,
                                  border: '1px solid',
                                  borderColor: 'info.200',
                                }}
                              >
                                <Typography
                                  variant="body2"
                                  sx={{ fontWeight: 500, mb: 1, color: 'info.main' }}
                                >
                                  Notes for repair shop:
                                </Typography>
                                <Typography variant="body2" sx={{ color: 'text.secondary', whiteSpace: 'pre-wrap' }}>
                                  {vehicle_damage.repair_shop_notes.trim()}
                                </Typography>
                              </Box>
                            ) : null}

                            <Box sx={{ mt: 2, pt: 2, borderTop: '1px solid', borderColor: 'divider' }}>
                              <Typography variant="caption" color="text.secondary">
                                Reported on:{' '}
                                {vehicle_damage.reported_at?.toDate?.()?.toLocaleDateString() || 'Unknown date'}
                              </Typography>
                            </Box>
                          </Box>
                        </CardContent>
                      </Card>
                    )}
                  </>
                )}
              </Box>
            )}
          </>
        )}
      </DialogContent>

      <PhotoComparisonDialog
        open={comparisonState.isOpen}
        onClose={() => setComparisonState({ isOpen: false, photoIndex: 0, mediaType: 'photo', flow: 'pre' })}
        photos={getComparisonPhotos().map((p) => ({
          id: p.id,
          url: p.url || p.storagePath || '',
          angle: p.angle,
          vehicleInfo: activeLeg ? `${activeLeg.fromLabel} → ${activeLeg.toLabel}` : '',
          mediaType: (p.mediaType as 'photo' | 'video') ?? 'photo',
        }))}
        currentPhotoIndex={comparisonState.photoIndex}
        onPhotoChange={(idx) => setComparisonState((s) => ({ ...s, photoIndex: idx }))}
      />
    </Dialog>
  )
}

const ProgressCard: React.FC<{
  progress: { completedMedia: number; totalMedia: number; percentage: number }
  label: string
}> = ({ progress, label }) => (
  <Card sx={{ mb: 2 }}>
    <CardContent>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
        <Typography variant="subtitle1" fontWeight={600}>
          {label} Progress
        </Typography>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
          <Chip
            icon={<CheckCircleIcon />}
            label={`${progress.completedMedia}/${progress.totalMedia} Media`}
            color={progress.percentage === 100 ? 'success' : progress.percentage > 0 ? 'warning' : 'default'}
            variant="outlined"
            size="small"
          />
          <Typography variant="body2" color="text.secondary">
            {progress.percentage}% Complete
          </Typography>
        </Box>
      </Box>
      <LinearProgress
        variant="determinate"
        value={progress.percentage}
        sx={{
          height: 8,
          borderRadius: 4,
          bgcolor: 'grey.200',
          '& .MuiLinearProgress-bar': {
            borderRadius: 4,
            bgcolor: progress.percentage === 100 ? 'success.main' : 'warning.main',
          },
        }}
      />
    </CardContent>
  </Card>
)

export default LegPhotosDialog
